<script lang="ts" setup>
import { useAuth } from "~/store/auth";
import { useQuery } from "@tanstack/vue-query";
import { IKenticoResponse } from "~/types/kentico/content";
import { StorageUtil } from "~/utils/storageUtil";
import { useSettings } from "~/store/settings";
import api from "~/utils/auth/api";

const settings = useSettings();
const visible = ref(false);
const toast = ref<string | null | undefined>(null);
const toastPath = ref("");
const isLoading = ref(false);
const toastContainer = ref<HTMLElement | null>(null);
const shouldFetchContent = ref(false);
const auth = useAuth();
const route = useRoute();
const config = useRuntimeConfig();

interface IToasts {
	contentPath: string;
	id: number;
	name: string;
}

interface IContent {
	content: string;
}

export interface Toast {
	toastId: number;
	toastStartDate: string;
	toastEndDate: string;
	marketingOptIn: string;
}

const shouldShowToast = computed(() => {
	if (
		route.query.auth === "login" ||
		route.query.login === "1" ||
		route.query.login === "true" ||
		route.query.auth === "register" ||
		route.query.register === "1" ||
		route.query.register === "true"
	) {
		return false;
	}
	return true;
});

const store = useSettings();
const { refetch } = useQuery({
	queryKey: ["toast", auth.loggedIn],
	staleTime: 0,
	cacheTime: 0,
	queryFn: () =>
		api({
			url: `/Toast/${store.config?.regionCode}`,
			type: auth.loggedIn ? "api" : "apic",
			options: {
				method: "GET",
			},
		}),
	refetchOnWindowFocus: false,
	enabled: computed(() => auth.hydrated),
	refetchOnMount: "always",
	onSuccess: (data: IToasts[]) => {
		if (data && data.length > 0 && shouldShowToast.value) {
			const storage = new StorageUtil();
			const pastToasts = storage.getStorage("pastToasts") ? storage.getStorage("pastToasts") : [];
			const filterToasts = data.filter((data: IToasts) => pastToasts.every((v: IToasts) => v.contentPath !== data.contentPath));
			if (filterToasts && filterToasts.length > 0) {
				pastToasts.push(filterToasts[0]);
				storage.setStorage("pastToasts", JSON.stringify(pastToasts));
				toastPath.value = filterToasts[0].contentPath;
				shouldFetchContent.value = true;
			}
		}
	},
});

watchEffect(() => {
	if (auth.loggedIn && !auth.modals.register) refetch();
});

useQuery<IKenticoResponse>({
	queryKey: ["toast-content", toastPath],
	queryFn: () =>
		api({
			url: `?host={kenticoHost}&route=${toastPath.value}&lang={locale}`,
			type: "content",
			options: {
				method: "GET",
				headers: {},
			},
		}),
	enabled: shouldFetchContent,
	onSuccess: (data: IContent) => {
		toast.value = data.content;
		visible.value = true;
	},
});

const setCloseToast = () => {
	const closeBtn = toastContainer.value?.querySelector("#close-toast");
	if (closeBtn) {
		closeBtn.addEventListener("click", closeToast);
		closeBtn.setAttribute("class", "cursor-pointer");
	}
};

const closeToast = () => {
	const closeBtn = toastContainer.value?.querySelector("#close-toast");
	if (closeBtn) {
		closeBtn.removeEventListener("click", closeToast);
	}
	visible.value = false;
};

const setSelectOffer = () => {
	const singUpOfferBtnEl = toastContainer.value?.getElementsByClassName("sign-up-offer-btn");
	if (singUpOfferBtnEl) {
		for (let i = 0; i < singUpOfferBtnEl.length; i++) {
			singUpOfferBtnEl[i].addEventListener("click", (event) => {
				const targetElement = event.target as HTMLElement;
				createTag(targetElement.id);
			});
		}
	}
};

const createTag = async (offer: string) => {
	isLoading.value = true;
	try {
		await api({
			url: `/Tagging/AddOrEditTagOnVuvuzelaAccount?regionCode=${settings.config?.brandSuffix as string}`,
			type: "api",
			apiVersion: "v2",
			options: {
				method: "POST",
				headers: { Authorization: auth.token ? `Bearer ${auth.token}` : "" },
			},
			data: {
				accountId: auth.profile?.id,
				tagName: "WelcomeOffer",
				value: offer,
			},
		});
	} catch (error) {
		console.error({ error });
	} finally {
		isLoading.value = true;
		visible.value = false;
	}
};

const renderContentScripts = () => {
	if (toastContainer.value) {
		const scriptsTags = toastContainer.value?.getElementsByTagName("script");
		for (let i = 0; i < scriptsTags?.length; i++) {
			const newScriptTag = document.createElement("script");
			const head = document.getElementById("scripts-holder");
			newScriptTag.type = "text/javascript";
			newScriptTag.text = scriptsTags[i].text;
			if (head) head.appendChild(newScriptTag);
			scriptsTags[i].innerHTML = "";
		}
	}
};

watchEffect(() => {
	if (toastContainer.value) {
		setCloseToast();
		setSelectOffer();
		renderContentScripts();
	}
});
</script>

<template>
	<UiModal :active="visible" :contentBackgroundClass="'rounded-lg'" :transparent="true">
		<div ref="toastContainer" v-html="toast" class="text-dark-900 light toast-container"></div>
	</UiModal>
</template>

<style lang="scss">
.row {
	@apply flex flex-row  grid-cols-12;
}
.col {
	@apply w-8/12 gap-2;
}
.v-btn {
	@apply inline-block w-11/12 rounded text-sm m-3 p-2  bg-primary-600 text-light-50 hover:bg-primary-500 focus:bg-primary-400;
}
</style>
